import { useCallback, useId, useMemo } from 'react'
import qs from 'qs'
import { pushWithRegion } from 'actions/NavigationActions'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import { useGlobalCarHireSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalCarHireSearch'
import useSearchVerticalLabels from 'hooks/GlobalSearch/useSearchVerticalLabels'
import CarHireTabContent from '../Components/Mobile/TabContents/CarHireTabContent'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import LineCarIcon from 'LuxKit/Icons/line/LineCarIcon'
import useQueryParams from 'hooks/useQueryParams'
import { getCarHireSearchType } from 'components/CarHire/carHireUtils'
import { useAppDispatch } from 'hooks/reduxHooks'
import { trackEvent } from 'analytics/analytics'
import { searchEventWithContext } from 'analytics/snowplow/events'
import { mapGlobalSearchContextToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'

function useGlobalSearchVerticalTabCarHire(): App.GlobalSearchVerticalTabV2 {
  const dispatch = useAppDispatch()
  const queryParams = useQueryParams()

  const {
    globalCarHireSearchDispatch: searchDispatch,
    globalCarHireSearchState: state,
  } = useGlobalCarHireSearchContext(GLOBAL_SEARCH_INITIAL_STATE, queryParams)

  const {
    searchItem,
    secondarySearchItem,
    pickUpTime,
    dropOffTime,
    checkinDate: pickUpDate,
    checkoutDate: dropOffDate,
    driversAgeCategory,
    driversAge,
  } = state

  const onSearch = useCallback(() => {
    if (!searchItem || !pickUpDate || !dropOffDate) {
      return
    }

    const pickUpItem = searchItem ?? ANYWHERE_SEARCH_ITEM
    const dropOffItem = secondarySearchItem ?? searchItem

    trackEvent(searchEventWithContext({
      searchEvent: { type: 'search_bar' },
      sortContext: { value: 'recommended' },
      searchContext:
      mapGlobalSearchContextToSnowplowSearchEvent({
        searchItem: pickUpItem,
        secondarySearchItem: dropOffItem,
        searchVerticals: new Set(['car-hire']),
        pickUpTime,
        dropOffTime,
        driversAgeCategory,
        checkinDate: pickUpDate,
        checkoutDate: dropOffDate,
      },
      ),
    }))

    const query = qs.stringify({
      pickUpName: pickUpItem.format.mainText,
      dropOffName: dropOffItem.format.mainText,
      pickUpId: pickUpItem.value,
      dropOffId: dropOffItem.value,
      pickUpDate: pickUpDate.format(ISO_DATE_FORMAT),
      pickUpTime,
      dropOffDate: dropOffDate.format(ISO_DATE_FORMAT),
      dropOffTime,
      ageCategory: driversAgeCategory,
      age: (driversAgeCategory === 1 || driversAgeCategory === 3) ? driversAge : undefined,
      pickUpSearchType: getCarHireSearchType(pickUpItem),
      dropOffSearchType: getCarHireSearchType(dropOffItem),
    })

    dispatch(pushWithRegion('/search/car-hire', query))
  }, [dispatch, driversAge, driversAgeCategory, dropOffDate, dropOffTime, pickUpDate, pickUpTime, searchItem, secondarySearchItem])
  const formId = useId()

  const verticalLabels = useSearchVerticalLabels()

  return useMemo((): App.GlobalSearchVerticalTabV2 => ({
    formId,
    label: verticalLabels['car-hire'],
    key: 'car-hire',
    context: { dispatch: searchDispatch, state },
    onSearch,
    ContentComponent: CarHireTabContent,
    Icon: LineCarIcon,
  }), [formId, searchDispatch, state, onSearch, verticalLabels])
}

export default useGlobalSearchVerticalTabCarHire
